/**
 * EMA Validation Tool
 * Compare our library calculation against AlphaVantage to identify discrepancies
 */

import { EMA } from 'technicalindicators';

/**
 * Test EMA calculation against known values
 */
export async function validateEMA(ticker, period = 20, numBars = 500) {
  console.log(`\n🔍 Validating EMA(${period}) for ${ticker} with ${numBars} bars`);
  
  try {
    // 1. Fetch TradeStation bars
    const barsResponse = await fetch(
      `/api/tradestation/marketdata/barcharts/${ticker}?` +
      `interval=1&unit=Daily&barsback=${numBars}&sessiontemplate=USEQPreAndPost`
    );
    const tsData = await barsResponse.json();
    const tsBars = tsData.Bars || [];
    
    console.log(`📊 Fetched ${tsBars.length} bars from TradeStation`);
    
    // 2. Fetch AlphaVantage EMA
    const avResponse = await fetch(
      `/api/indicators/getIndicator?` +
      `function=EMA&symbol=${ticker}&interval=daily&time_period=${period}&series_type=close`
    );
    const avData = await avResponse.json();
    
    // Parse AV data
    const avKey = Object.keys(avData).find(k => k.includes('Technical'));
    if (!avKey) {
      console.error('❌ No AlphaVantage data found');
      return null;
    }
    
    const avValues = avData[avKey];
    const avPoints = Object.entries(avValues)
      .map(([date, values]) => ({
        date,
        time: Math.floor(new Date(date + 'T00:00:00Z').getTime() / 1000),
        value: parseFloat(values.EMA)
      }))
      .sort((a, b) => a.time - b.time);
    
    console.log(`📊 Fetched ${avPoints.length} EMA values from AlphaVantage`);
    
    // 3. Calculate our EMA
    const closes = tsBars.map(b => b.Close);
    const emaResults = EMA.calculate({
      period: period,
      values: closes
    });
    
    const ourPoints = emaResults.map((value, idx) => {
      const barIdx = tsBars.length - emaResults.length + idx;
      return {
        time: tsBars[barIdx].Time,
        value: value,
        close: tsBars[barIdx].Close
      };
    });
    
    console.log(`📊 Calculated ${ourPoints.length} EMA values from library`);
    
    // 4. Compare the last 20 values (most recent)
    console.log('\n📈 Comparison (Last 20 bars):');
    console.log('Date\t\t\tClose\t\tOur EMA\t\tAV EMA\t\tDiff\t\t% Diff');
    console.log('='.repeat(100));
    
    const comparisons = [];
    const compareCount = Math.min(20, ourPoints.length);
    
    for (let i = ourPoints.length - compareCount; i < ourPoints.length; i++) {
      const ourPoint = ourPoints[i];
      const date = new Date(ourPoint.time * 1000).toISOString().split('T')[0];
      
      // Find matching AV point (allow 1 day tolerance)
      const avPoint = avPoints.find(p => 
        Math.abs(p.time - ourPoint.time) < 86400
      );
      
      if (avPoint) {
        const diff = ourPoint.value - avPoint.value;
        const pctDiff = (diff / avPoint.value) * 100;
        
        comparisons.push({
          date,
          close: ourPoint.close,
          ourValue: ourPoint.value,
          avValue: avPoint.value,
          diff,
          pctDiff
        });
        
        const diffStr = diff >= 0 ? `+${diff.toFixed(4)}` : diff.toFixed(4);
        const pctStr = pctDiff >= 0 ? `+${pctDiff.toFixed(3)}%` : `${pctDiff.toFixed(3)}%`;
        const status = Math.abs(pctDiff) < 0.1 ? '✅' : Math.abs(pctDiff) < 1 ? '⚠️' : '❌';
        
        console.log(
          `${date}\t${ourPoint.close.toFixed(2)}\t\t${ourPoint.value.toFixed(4)}\t\t${avPoint.value.toFixed(4)}\t\t${diffStr}\t${pctStr}\t${status}`
        );
      }
    }
    
    // 5. Calculate aggregate statistics
    if (comparisons.length > 0) {
      const avgDiff = comparisons.reduce((sum, c) => sum + Math.abs(c.pctDiff), 0) / comparisons.length;
      const maxDiff = Math.max(...comparisons.map(c => Math.abs(c.pctDiff)));
      const goodCount = comparisons.filter(c => Math.abs(c.pctDiff) < 0.1).length;
      
      console.log('\n📊 Statistics:');
      console.log(`Average Difference: ${avgDiff.toFixed(3)}%`);
      console.log(`Maximum Difference: ${maxDiff.toFixed(3)}%`);
      console.log(`Within 0.1%: ${goodCount}/${comparisons.length} (${(goodCount/comparisons.length*100).toFixed(1)}%)`);
      
      if (avgDiff < 0.1) {
        console.log('\n✅ EXCELLENT: Values match AlphaVantage closely!');
      } else if (avgDiff < 1) {
        console.log('\n⚠️ ACCEPTABLE: Small variance, likely due to warm-up period');
      } else {
        console.log('\n❌ PROBLEMATIC: Large variance detected');
      }
      
      return {
        avgDiff,
        maxDiff,
        accuracy: goodCount / comparisons.length,
        comparisons
      };
    }
    
    return null;
  } catch (error) {
    console.error('❌ Validation error:', error);
    return null;
  }
}

// Make it available on window for console testing
if (typeof window !== 'undefined') {
  window.validateEMA = validateEMA;
}


